import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Send, Loader2, GraduationCap, Lightbulb, Plus } from 'lucide-react';
import { getFeedupGreeting, getAIFeedback, chatWithDrafty, getSentenceStarters, logFeedback, getStudentFeedback } from '../lib/api';
import { DraftyMascot } from './DraftyMascot';
import { useRotatingStatus } from '../lib/useRotatingStatus';
import { toast } from 'sonner';

// Progress messaging for the coach wait (R10). Each step names work the
// request is actually doing: the draft, the prompt, and the rubric are all
// sent as context, and the coach's job is to land on ONE thing to work on.
const COACH_WAIT_STEPS = [
  'Reading what you wrote…',
  'Thinking about your assignment…',
  'Finding the one thing worth working on…',
];

/**
 * CoachPanel — the single coach conversation beside the student's writing.
 *
 * One thread, three kinds of turns:
 *  - the cached feed-up greeting opens the conversation
 *  - "Coach, look at my draft" turns (structured GLOW/GROW feedback)
 *  - free-form questions to the coach
 * Sentence starters are a scaffold the coach offers inside the panel,
 * not a separate tool box.
 */
const CoachPanel = ({ draft, assignment, content, currentStage, writingType, profile, onInsert, nudge, onNudgeDone }) => {
  const [messages, setMessages] = useState([]);
  const [greetingLoading, setGreetingLoading] = useState(false);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  // Declared here, above every effect and the JSX that reads it (D23).
  const coachWaitLabel = useRotatingStatus(isThinking, 'Drafty is thinking…', COACH_WAIT_STEPS);
  const [starters, setStarters] = useState(null);
  const [startersLoading, setStartersLoading] = useState(false);
  const [showStarters, setShowStarters] = useState(false);
  const scrollRef = useRef(null);

  // Feed-up greeting opens the thread. Cached server-side per draft;
  // any failure falls back to a warm static welcome — the panel is never empty.
  useEffect(() => {
    let cancelled = false;
    const staticFallback =
      `Welcome${assignment?.title ? ` to "${assignment.title}"` : ''}! I'm Drafty, your writing coach.\n\n` +
      "I'll ask questions, challenge your thinking, and cheer you on — but I'll never write it for you. That's your voice.\n\n" +
      'What is this prompt really asking you to do — in your own words?\n\n' +
      "Jot your thinking down, then start your first sentence. I'm right here when you want feedback.";

    // Teacher preview / no persisted draft: open with the static greeting so
    // the panel shows exactly what a student's first visit feels like.
    // The greeting always PREPENDS (preserving any replayed feedback turns
    // the released-feedback effect may have already loaded) — never replaces.
    const setGreeting = (text) => setMessages(prev => [
      { role: 'assistant', kind: 'greeting', content: text },
      ...prev.filter(m => m.kind !== 'greeting'),
    ]);

    if (draft && !draft.id) {
      setGreeting(staticFallback);
      return;
    }
    if (!draft?.id) return;

    if (draft.coach_greeting) {
      setGreeting(draft.coach_greeting);
      return;
    }
    const load = async () => {
      setGreetingLoading(true);
      try {
        const res = await getFeedupGreeting(draft.id);
        if (!cancelled) setGreeting(res?.greeting || staticFallback);
      } catch {
        if (!cancelled) setGreeting(staticFallback);
      } finally {
        if (!cancelled) setGreetingLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [draft?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Released coach feedback persists in feedback_logs — replay it in the
  // thread on open, so FlowGrade releases actually REACH the student on
  // their next visit (the release loop's delivery surface).
  useEffect(() => {
    if (!draft?.id || !profile?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await getStudentFeedback(profile.id);
        if (cancelled || !Array.isArray(rows)) return;  // demo mode returns an object — skip
        const seen = new Set();
        const turns = rows
          .filter(r => r.draft_id === draft.id && (r.review_status ?? 'released') === 'released')
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
          .filter(r => {
            // one row per CCSS standard shares text + created_at — dedupe to items
            const key = `${r.created_at}|${r.feedback_text}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .map(r => ({ role: 'assistant', kind: 'feedback', content: r.feedback_text }));
        if (turns.length && !cancelled) {
          setMessages(prev => [
            ...prev.filter(m => m.kind === 'greeting'),
            ...turns,
            ...prev.filter(m => m.kind !== 'greeting'),
          ].filter((m, i, arr) => arr.indexOf(m) === i));
        }
      } catch (e) {
        console.error('Released feedback fetch failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [draft?.id, profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the newest turn in view
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, isThinking]);

  const aiErrorText = (error) => {
    const status = error?.response?.status;
    if (status === 502) return 'The AI service returned an error. Try again in a moment.';
    if (status === 503) return 'The AI service is temporarily unavailable. Try again shortly.';
    if (status === 429) return "We've hit the AI rate limit. Wait a moment and try again.";
    return 'Something went wrong. Mind trying that again?';
  };

  // "Coach, look at my draft" — structured feedback lands in the thread
  const requestFeedback = async () => {
    if (!content?.trim() || isThinking) return;
    setMessages(prev => [...prev, { role: 'user', kind: 'feedback-request', content: 'Coach, look at my draft.' }]);
    setIsThinking(true);
    try {
      const response = await getAIFeedback(
        content,
        assignment?.prompt || 'General writing assignment',
        assignment?.ccss_standards || [],
        'comprehensive',
        currentStage,
        draft?.id || ''
      );
      if (response.review_status === 'pending_review') {
        // FlowGrade review mode: the coach's feedback goes to the teacher
        // first. The student gets a warm held-state turn, not the text.
        setMessages(prev => [...prev, {
          role: 'assistant',
          kind: 'held',
          content: "I just read your draft and sent my feedback to your teacher to look over first. It'll appear here as soon as they release it — keep writing in the meantime!",
        }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', kind: 'feedback', content: response.feedback }]);
      }
      // Persist to feedback_logs for the analytics pipeline (non-blocking).
      // Skipped when the backend already persisted (FlowGrade, logged: true)
      // and in teacher preview (no persisted draft to log against).
      try {
        if (response.logged) throw new Error('backend persisted — skipping client log');
        if (!draft?.id) throw new Error('preview — skipping feedback log');
        const standards = assignment?.ccss_standards || ['General'];
        for (const std of standards) {
          await logFeedback({
            draft_id: draft.id,
            student_id: profile.id,
            feedback_type: 'comprehensive',
            ccss_standard: std,
            feedback_text: response.feedback,
          });
        }
      } catch (logError) {
        if (!/skipping/.test(String(logError?.message))) {
          console.error('Failed to log feedback:', logError);
        }
      }
    } catch (error) {
      console.error('Feedback error:', error);
      toast.error(aiErrorText(error));
      setMessages(prev => [...prev, { role: 'assistant', kind: 'error', content: aiErrorText(error) }]);
    } finally {
      setIsThinking(false);
    }
  };

  // Free-form question in the same thread. sendText carries the shared path
  // so milestone nudges (opt-in check-ins) reuse the exact chat pipeline.
  const sendText = async (text) => {
    if (!text?.trim() || isThinking) return;
    const userMessage = { role: 'user', kind: 'chat', content: text };
    const history = [...messages, userMessage]
      .filter(m => m.kind !== 'error')
      .map(({ role, content: c }) => ({ role, content: c }));
    setMessages(prev => [...prev, userMessage]);
    setIsThinking(true);
    try {
      const response = await chatWithDrafty(history, content || null, draft?.id || null);
      setMessages(prev => [...prev, { role: 'assistant', kind: 'chat', content: response.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { role: 'assistant', kind: 'error', content: aiErrorText(error) }]);
    } finally {
      setIsThinking(false);
    }
  };

  const sendMessage = () => {
    if (!input.trim()) return;
    const text = input;
    setInput('');
    sendText(text);
  };

  const acceptNudge = () => {
    if (!nudge) return;
    const question = nudge.question;
    onNudgeDone?.();
    sendText(question);
  };

  // Sentence-starter scaffold — coach-offered, rendered inside the panel
  const loadStarters = async () => {
    if (startersLoading) return;
    setShowStarters(true);
    if (starters) return;
    setStartersLoading(true);
    try {
      const res = await getSentenceStarters(
        assignment?.prompt || '',
        assignment?.ccss_standards || [],
        currentStage,
        writingType
      );
      setStarters(res?.starters || res || []);
    } catch (e) {
      console.error('Starters error:', e);
      toast.error('Could not load sentence starters right now.');
      setShowStarters(false);
    } finally {
      setStartersLoading(false);
    }
  };

  const starterList = Array.isArray(starters) ? starters : [];

  return (
    <div className="h-full bg-slate-800 rounded-xl flex flex-col overflow-hidden" data-testid="coach-panel">
      {/* Header */}
      <div className="p-4 border-b border-slate-700 flex items-center gap-3">
        <DraftyMascot size="sm" mood="excited" className="!w-10 !h-10" />
        <div>
          <h3 className="font-heading text-lg text-white flex items-center gap-2">
            Drafty
            <Sparkles className="text-ai-accent" size={16} />
          </h3>
          <p className="text-slate-400 text-xs">Your writing coach</p>
        </div>
      </div>

      {/* Thread */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {/* Teacher feedback pinned above the AI thread */}
        {draft?.teacher_feedback && (
          <div className="card-surface border-l-4 border-teal-flow p-3" data-testid="teacher-feedback">
            <h4 className="text-white text-sm font-medium flex items-center gap-2 mb-1">
              <GraduationCap className="text-teal-flow" size={14} />
              Teacher Feedback
            </h4>
            <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">
              {typeof draft.teacher_feedback === 'string' ? draft.teacher_feedback : JSON.stringify(draft.teacher_feedback)}
            </p>
            {draft.teacher_feedback_at && (
              <p className="text-slate-500 text-xs mt-1">— Your Teacher · {new Date(draft.teacher_feedback_at).toLocaleString()}</p>
            )}
          </div>
        )}

        {greetingLoading && messages.length === 0 && (
          <p className="text-slate-400 text-sm flex items-center gap-2" data-testid="coach-greeting-loading">
            <Loader2 size={14} className="animate-spin" />
            Drafty is reading your assignment…
          </p>
        )}

        {messages.map((msg, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            data-testid={msg.kind === 'greeting' ? 'coach-greeting' : undefined}
          >
            <div
              className={`max-w-[90%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-flow-blue text-white rounded-br-none'
                  : msg.kind === 'greeting'
                  ? 'card-surface border-l-4 border-ai-accent text-slate-300 rounded-none w-full'
                  : msg.kind === 'held'
                  ? 'card-surface border-l-4 border-gold-bright text-slate-300 rounded-none w-full'
                  : 'bg-slate-700 text-slate-100 rounded-bl-none'
              }`}
            >
              {msg.content}
            </div>
          </motion.div>
        ))}

        {isThinking && (
          <div className="flex justify-start">
            <div className="bg-slate-700 rounded-2xl px-4 py-3 rounded-bl-none flex items-center gap-2">
              <Loader2 size={14} className="animate-spin text-purple-400" />
              <span className="text-slate-400 text-sm" aria-live="polite">{coachWaitLabel}</span>
            </div>
          </div>
        )}

        {/* Sentence-starter scaffold */}
        {showStarters && (
          <div className="card-surface p-3" data-testid="starter-scaffold">
            <h4 className="text-gold-bright text-xs font-medium flex items-center gap-2 mb-2">
              <Lightbulb size={14} /> Sentence starters — click to add one to your draft
            </h4>
            {startersLoading ? (
              <p className="text-slate-400 text-sm flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Finding starters for this assignment…
              </p>
            ) : (
              <div className="flex flex-col gap-1">
                {starterList.map((s, i) => {
                  const text = typeof s === 'string' ? s : s.text || s.starter || '';
                  return (
                    <button
                      key={i}
                      onClick={() => { onInsert?.(text); toast.success('Added to your draft — now finish the thought!'); }}
                      className="text-left text-slate-300 text-sm px-2 py-1.5 rounded-lg hover:bg-slate-700 flex items-start gap-2"
                    >
                      <Plus size={14} className="text-flow-blue-hover mt-0.5 flex-shrink-0" />
                      {text}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Milestone nudge — opt-in check-in (Phase 5 item 3). Never sends
          anything unless the student clicks; dismiss costs one click. */}
      {nudge && (
        <div className="mx-3 mb-2 card-surface border-l-4 border-gold-bright p-3 flex items-start gap-2" data-testid="milestone-nudge">
          <Lightbulb size={16} className="text-gold-bright mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-slate-300 text-sm leading-snug">{nudge.label}</p>
            <div className="flex gap-2 mt-2">
              <button
                data-testid="nudge-accept"
                onClick={acceptNudge}
                disabled={isThinking}
                className="btn-purple px-3 py-1.5 rounded-lg text-xs disabled:opacity-50"
              >
                Check in with Drafty
              </button>
              <button
                data-testid="nudge-dismiss"
                onClick={() => onNudgeDone?.()}
                className="text-slate-400 hover:text-white text-xs px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Actions + input */}
      <div className="p-3 border-t border-slate-700 space-y-2">
        <div className="flex gap-2">
          <button
            data-testid="get-feedback-btn"
            onClick={requestFeedback}
            disabled={isThinking || !content?.trim()}
            className="flex-1 btn-purple py-2 rounded-lg flex items-center justify-center gap-2 text-sm disabled:opacity-50"
          >
            <Sparkles size={16} />
            Coach, look at my draft
          </button>
          <button
            data-testid="starters-btn"
            onClick={loadStarters}
            disabled={startersLoading}
            title="Sentence starters"
            className="px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-gold-bright disabled:opacity-50"
          >
            <Lightbulb size={16} />
          </button>
        </div>
        <div className="flex gap-2">
          <input
            data-testid="coach-chat-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Ask Drafty anything about your writing..."
            className="flex-1 bg-slate-900 border border-slate-600 rounded-xl px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-ai-accent focus:border-transparent"
          />
          <button
            data-testid="coach-chat-send"
            onClick={sendMessage}
            disabled={isThinking || !input.trim()}
            className="bg-ai-accent text-white rounded-xl px-3 py-2 disabled:opacity-50 hover:bg-ai-accent-hover transition-colors"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CoachPanel;
